import { GraphQLError } from 'graphql';
import { graphql, GraphQLRequest, ResponseResolver } from 'msw';
import { defaultContext } from 'msw/lib/types/handlers/RequestHandler';

import {
  AddUserMutation,
  ArchiveDirectoryMutation,
  ArchiveFileByIdMutation,
  BlockUserMutation,
  CreateFolderMutation,
  DeleteFileByIdMutation,
  DownloadFileQuery,
  DownloadFileVersionQuery,
  GetAppConfigQuery,
  GetDataRoomsQuery,
  GetDirectoryQuery,
  GetFileActivityEventsQuery,
  GetFileMoveDirectoryQuery,
  GetFileQuery,
  GetFileVersionHistoryQuery,
  GetMeQuery,
  GetProblemFilesQuery,
  GetSearchResultsQuery,
  GetTreatyNameQuery,
  GetTreatyUserQuery,
  GetUserManagementDataQuery,
  MoveFileMutation,
  PiiApproveByIdMutation,
  PiiJustifyByIdMutation,
  RenameFileMutation,
  ResetMfaMutation,
  RetryFileProcessesMutation,
  UnblockUserMutation,
  UpdateUserEmailNotificationsMutation,
  UpdateUserRolesMutation,
  UploadFileSignedUrlMutation,
  ValidateFilenamesMutation,
} from './generated';

type OmitTypename<T> = Omit<T, '__typename'>;

export type GQLContextType = typeof defaultContext & {
  // missing from official typedefs
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data(d: any): any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors(e: any[]): any;
};

type MockTuple<D = unknown> = {
  data: ((args?: unknown) => D) | undefined;
  errors: (() => GraphQLError[]) | undefined;
};

const MocksRecord: Partial<Record<OperationName, MockTuple>> = {};

const makeMock = <D>(
  mockName: OperationName,
  data?: D | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<D> => {
  const dataMock = data && (() => data);
  const errorsMock = errors && (() => errors);
  const mockEntry = {
    data: dataMock,
    errors: errorsMock,
  };
  MocksRecord[mockName] = mockEntry;
  return mockEntry;
};

export const genericGQLHandler: ResponseResolver<
  GraphQLRequest<never>,
  GQLContextType
> = (req, res, ctx) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const operationName: OperationName | undefined = req.body?.operationName; // does seem to exist
  if (operationName) {
    const mock = MocksRecord[operationName];

    if (mock?.errors) {
      return res(ctx.errors(mock.errors()));
    }
    if (mock?.data) {
      return res(ctx.data(mock.data(req.body?.variables)));
    }
    if (MocksRecord[operationName]) {
      return res(ctx.data({}));
    }
  }

  // passthrough
  return void 0;
};

export const resetMock = (mockName: OperationName): void => {
  MocksRecord[mockName] = undefined;
};

export const resetMocks = (): void => {
  Object.keys(MocksRecord).forEach((k) => {
    resetMock((k as unknown) as OperationName);
  });
};

export enum QueryOperationName {
  GetFileActivityEvents = 'GetFileActivityEvents',
  GetFileMoveDirectory = 'GetFileMoveDirectory',
  DownloadFile = 'DownloadFile',
  GetMe = 'GetMe',
  DownloadFileVersion = 'DownloadFileVersion',
  GetFileVersionHistory = 'GetFileVersionHistory',
  GetSearchResults = 'GetSearchResults',
  GetTreatyUser = 'GetTreatyUser',
  GetAppConfig = 'GetAppConfig',
  GetTreatyName = 'GetTreatyName',
  GetDataRooms = 'GetDataRooms',

  GetDirectory = 'GetDirectory',
  GetFile = 'GetFile',
  GetProblemFiles = 'GetProblemFiles',
  GetUserManagementData = 'GetUserManagementData',
}

export enum MutationOperationName {
  AddUser = 'AddUser',
  ArchiveDirectory = 'ArchiveDirectory',
  ArchiveFileById = 'ArchiveFileById',
  DeleteFileById = 'DeleteFileById',
  MoveFile = 'MoveFile',
  RenameFile = 'RenameFile',
  PiiApproveById = 'PiiApproveById',
  PiiJustifyById = 'PiiJustifyById',
  RetryFileProcesses = 'RetryFileProcesses',
  ResetMFA = 'ResetMFA',
  UpdateUserEmailNotifications = 'UpdateUserEmailNotifications',
  UpdateUserRoles = 'UpdateUserRoles',
  BlockUser = 'BlockUser',
  UnblockUser = 'UnblockUser',
  CreateFolder = 'CreateFolder',
  UploadFileSignedUrl = 'UploadFileSignedUrl',
  ValidateFilenames = 'ValidateFilenames',
}

type OperationName = QueryOperationName | MutationOperationName;

export const makeGetFileActivityEventsMock = (
  data?: OmitTypename<GetFileActivityEventsQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetFileActivityEventsQuery>> =>
  makeMock(QueryOperationName.GetFileActivityEvents, data, errors);

export const makeGetFileMoveDirectoryMock = (
  data?: OmitTypename<GetFileMoveDirectoryQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetFileMoveDirectoryQuery>> =>
  makeMock(QueryOperationName.GetFileMoveDirectory, data, errors);

export const makeDownloadFileMock = (
  data?: OmitTypename<DownloadFileQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<DownloadFileQuery>> =>
  makeMock(QueryOperationName.DownloadFile, data, errors);

export const makeGetMeMock = (
  data?: OmitTypename<GetMeQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetMeQuery>> =>
  makeMock(QueryOperationName.GetMe, data, errors);

export const makeDownloadFileVersionMock = (
  data?: OmitTypename<DownloadFileVersionQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<DownloadFileVersionQuery>> =>
  makeMock(QueryOperationName.DownloadFileVersion, data, errors);

export const makeGetFileVersionHistoryMock = (
  data?: OmitTypename<GetFileVersionHistoryQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetFileVersionHistoryQuery>> =>
  makeMock(QueryOperationName.GetFileVersionHistory, data, errors);

export const makeGetSearchResultsMock = (
  data?: OmitTypename<GetSearchResultsQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetSearchResultsQuery>> =>
  makeMock(QueryOperationName.GetSearchResults, data, errors);

export const makeGetTreatyUserMock = (
  data?: OmitTypename<GetTreatyUserQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetTreatyUserQuery>> =>
  makeMock(QueryOperationName.GetTreatyUser, data, errors);

export const makeGetAppConfigMock = (
  data?: OmitTypename<GetAppConfigQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetAppConfigQuery>> =>
  makeMock(QueryOperationName.GetAppConfig, data, errors);

export const makeGetTreatyNameMock = (
  data?: OmitTypename<GetTreatyNameQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetTreatyNameQuery>> =>
  makeMock(QueryOperationName.GetTreatyName, data, errors);

export const makeGetDataRoomsMock = (
  data?: OmitTypename<GetDataRoomsQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetDataRoomsQuery>> =>
  makeMock(QueryOperationName.GetDataRooms, data, errors);



export const makeGetDirectoryMock = (
  data?: OmitTypename<GetDirectoryQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetDirectoryQuery>> =>
  makeMock(QueryOperationName.GetDirectory, data, errors);

export const makeGetFileMock = (
  data?: OmitTypename<GetFileQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetFileQuery>> =>
  makeMock(QueryOperationName.GetFile, data, errors);

export const makeGetProblemFilesMock = (
  data?: OmitTypename<GetProblemFilesQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetProblemFilesQuery>> =>
  makeMock(QueryOperationName.GetProblemFiles, data, errors);

export const makeGetUserManagementDataMock = (
  data?: OmitTypename<GetUserManagementDataQuery> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<GetUserManagementDataQuery>> =>
  makeMock(QueryOperationName.GetUserManagementData, data, errors);

export const makeAddUserMock = (
  data?: OmitTypename<AddUserMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<AddUserMutation>> =>
  makeMock(MutationOperationName.AddUser, data, errors);

export const makeArchiveDirectoryMock = (
  data?: OmitTypename<ArchiveDirectoryMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<ArchiveDirectoryMutation>> =>
  makeMock(MutationOperationName.ArchiveDirectory, data, errors);

export const makeArchiveFileByIdMock = (
  data?: OmitTypename<ArchiveFileByIdMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<ArchiveFileByIdMutation>> =>
  makeMock(MutationOperationName.ArchiveFileById, data, errors);

export const makeDeleteFileByIdMock = (
  data?: OmitTypename<DeleteFileByIdMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<DeleteFileByIdMutation>> =>
  makeMock(MutationOperationName.DeleteFileById, data, errors);

export const makeMoveFileMock = (
  data?: OmitTypename<MoveFileMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<MoveFileMutation>> =>
  makeMock(MutationOperationName.MoveFile, data, errors);

export const makeRenameFileMock = (
  data?: OmitTypename<RenameFileMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<RenameFileMutation>> =>
  makeMock(MutationOperationName.RenameFile, data, errors);

export const makePiiApproveByIdMock = (
  data?: OmitTypename<PiiApproveByIdMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<PiiApproveByIdMutation>> =>
  makeMock(MutationOperationName.PiiApproveById, data, errors);

export const makePiiJustifyByIdMock = (
  data?: OmitTypename<PiiJustifyByIdMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<PiiJustifyByIdMutation>> =>
  makeMock(MutationOperationName.PiiJustifyById, data, errors);

export const makeRetryFileProcessesMock = (
  data?: OmitTypename<RetryFileProcessesMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<RetryFileProcessesMutation>> =>
  makeMock(MutationOperationName.RetryFileProcesses, data, errors);

export const makeResetMfaMock = (
  data?: OmitTypename<ResetMfaMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<ResetMfaMutation>> =>
  makeMock(MutationOperationName.ResetMFA, data, errors);

export const makeUpdateUserEmailNotificationsMock = (
  data?: OmitTypename<UpdateUserEmailNotificationsMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<UpdateUserEmailNotificationsMutation>> =>
  makeMock(MutationOperationName.UpdateUserEmailNotifications, data, errors);

export const makeUpdateUserRolesMock = (
  data?: OmitTypename<UpdateUserRolesMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<UpdateUserRolesMutation>> =>
  makeMock(MutationOperationName.UpdateUserRoles, data, errors);

export const makeBlockUserMock = (
  data?: OmitTypename<BlockUserMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<BlockUserMutation>> =>
  makeMock(MutationOperationName.BlockUser, data, errors);

export const makeUnblockUserMock = (
  data?: OmitTypename<UnblockUserMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<UnblockUserMutation>> =>
  makeMock(MutationOperationName.UnblockUser, data, errors);

export const makeCreateFolderMock = (
  data?: OmitTypename<CreateFolderMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<CreateFolderMutation>> =>
  makeMock(MutationOperationName.CreateFolder, data, errors);

export const makeUploadFileSignedUrlMock = (
  data?: OmitTypename<UploadFileSignedUrlMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<UploadFileSignedUrlMutation>> =>
  makeMock(MutationOperationName.UploadFileSignedUrl, data, errors);

export const makeValidateFilenamesMock = (
  data?: OmitTypename<ValidateFilenamesMutation> | undefined,
  errors?: GraphQLError[] | undefined
): MockTuple<OmitTypename<ValidateFilenamesMutation>> =>
  makeMock(MutationOperationName.ValidateFilenames, data, errors);

export const gqlHandlers = [
  graphql.query('GetFileActivityEvents', genericGQLHandler),
  graphql.query('GetFileMoveDirectory', genericGQLHandler),
  graphql.query('DownloadFile', genericGQLHandler),
  graphql.query('GetMe', genericGQLHandler),
  graphql.query('DownloadFileVersion', genericGQLHandler),
  graphql.query('GetFileVersionHistory', genericGQLHandler),
  graphql.query('GetSearchResults', genericGQLHandler),
  graphql.query('GetTreatyUser', genericGQLHandler),
  graphql.query('GetAppConfig', genericGQLHandler),
  graphql.query('GetTreatyName', genericGQLHandler),
  graphql.query('GetDataRooms', genericGQLHandler),

  graphql.query('GetDirectory', genericGQLHandler),
  graphql.query('GetFile', genericGQLHandler),
  graphql.query('GetProblemFiles', genericGQLHandler),
  graphql.query('GetUserManagementData', genericGQLHandler),
  graphql.mutation('AddUser', genericGQLHandler),
  graphql.mutation('ArchiveDirectory', genericGQLHandler),
  graphql.mutation('ArchiveFileById', genericGQLHandler),
  graphql.mutation('DeleteFileById', genericGQLHandler),
  graphql.mutation('MoveFile', genericGQLHandler),
  graphql.mutation('RenameFile', genericGQLHandler),
  graphql.mutation('PiiApproveById', genericGQLHandler),
  graphql.mutation('PiiJustifyById', genericGQLHandler),
  graphql.mutation('RetryFileProcesses', genericGQLHandler),
  graphql.mutation('ResetMFA', genericGQLHandler),
  graphql.mutation('UpdateUserEmailNotifications', genericGQLHandler),
  graphql.mutation('UpdateUserRoles', genericGQLHandler),
  graphql.mutation('BlockUser', genericGQLHandler),
  graphql.mutation('UnblockUser', genericGQLHandler),
  graphql.mutation('CreateFolder', genericGQLHandler),
  graphql.mutation('UploadFileSignedUrl', genericGQLHandler),
  graphql.mutation('ValidateFilenames', genericGQLHandler),
];
